
import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface DarkroomModeContextType {
  darkroomMode: boolean;
  toggleDarkroomMode: () => void;
  canNavigateAway: (targetPath: string) => boolean;
}

const DarkroomModeContext = createContext<DarkroomModeContextType | undefined>(undefined);

export const DarkroomModeProvider = ({ children }: { children: ReactNode }) => {
  const [darkroomMode, setDarkroomMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Monitor path changes to handle darkroom mode restrictions
  useEffect(() => {
    const isDevelopPath = location.pathname === '/develop';
    
    // If darkroom mode is on but user is not on develop page, turn it off
    if (darkroomMode && !isDevelopPath) {
      toast.info('Darkroom mode has been turned off as you navigated away from the development page.', {
        duration: 3000,
      });
      setDarkroomMode(false);
    }
  }, [location.pathname, darkroomMode]);
  
  // Apply darkroom mode effect to text
  useEffect(() => {
    if (darkroomMode) {
      // Find all example text elements and make them red
      document.querySelectorAll('.example-text').forEach(el => {
        (el as HTMLElement).style.color = '#ea384c';
      });
    } else {
      // Reset color when darkroom mode is off
      document.querySelectorAll('.example-text').forEach(el => {
        (el as HTMLElement).style.color = '';
      });
    }
  }, [darkroomMode]);
  
  const toggleDarkroomMode = () => {
    const isDevelopPath = location.pathname === '/develop';
    
    if (!isDevelopPath && !darkroomMode) {
      // If user tries to enable darkroom mode outside of develop page
      toast.warning('Darkroom mode is only available on the film development page.', {
        duration: 3000,
      });
      return;
    }
    
    setDarkroomMode(prev => !prev);
  };
  
  // New function to check if navigation away is allowed
  const canNavigateAway = (targetPath: string): boolean => {
    if (darkroomMode && location.pathname === '/develop' && targetPath !== '/develop') {
      toast.error('Please turn off darkroom mode before leaving the development page.', {
        duration: 3000,
      });
      return false;
    }
    return true;
  };
  
  return (
    <DarkroomModeContext.Provider value={{ darkroomMode, toggleDarkroomMode, canNavigateAway }}>
      {children}
    </DarkroomModeContext.Provider>
  );
};

export const useDarkroomMode = () => {
  const context = useContext(DarkroomModeContext);
  if (context === undefined) {
    throw new Error('useDarkroomMode must be used within a DarkroomModeProvider');
  }
  return context;
};
